<template>
  <div class="options-container">
    <b-overlay :show="isLoading" rounded="sm" spinner-variant="primary">
      <b-card class="mb-3 option-card" bg-variant="light" no-body>
        <b-card-body class="p-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <label for="option-title-default" class="form-label mb-0"
              >Tiêu đề</label
            >
          </div>
          <b-form-input
            id="option-title-default"
            v-model="productOptions[0].name"
            placeholder="V.d. Màu sắc, Kích cỡ, Chất liệu"
            :state="productOptions[0].touched ? validateOptionTitle(0) : null"
            @change="onOptionInput(0)"
            @blur="onOptionBlur(0)"
            class="mb-3"
          ></b-form-input>

          <label for="option-values-default" class="form-label mb-2"
            >Giá trị</label
          >
          <b-form-tags
            id="option-values-default"
            v-model="productOptions[0].optionValues"
            placeholder="Nhập giá trị và nhấn enter (V.d. Đen, Trắng, Xanh)"
            :tag-validator="validateTag"
            tag-variant="primary"
            class="mb-2"
            add-button-text="Thêm"
            tag-placeholder="Nhấn enter để thêm"
            duplicates-input-field="true"
            :state="productOptions[0].touched ? validateOptionValues(0) : null"
            @input="onTagsUpdate(0, $event)"
            no-outer-focus
          ></b-form-tags>

          <b-form-invalid-feedback
            v-if="productOptions[0].touched && !validateOptionValues(0)"
          >
            Please add at least one value
          </b-form-invalid-feedback>

          <b-form-invalid-feedback
            v-if="productOptions[0].touched && !validateOptionTitle(0)"
          >
            Option title is required
          </b-form-invalid-feedback>
        </b-card-body>
      </b-card>

      <b-card
        v-for="(option, index) in productOptions.slice(1)"
        :key="`option-${index + 1}`"
        class="mb-3 option-card"
        bg-variant="light"
        no-body
      >
        <b-card-body
          class="d-flex justify-content-between align-items-center p-3"
        >
          <div class="left flex-grow-1">
            <label :for="`option-title-${index + 1}`" class="form-label mb-0"
              >Tiêu đề</label
            >
            <b-form-input
              :id="`option-title-${index + 1}`"
              v-model="option.name"
              placeholder="V.d. Màu sắc, Kích cỡ, Chất liệu"
              :state="option.touched ? validateOptionTitle(index + 1) : null"
              @change="onOptionInput(index + 1)"
              @blur="onOptionBlur(index + 1)"
              class="mb-3"
            ></b-form-input>

            <label :for="`option-values-${index + 1}`" class="form-label mb-2"
              >Giá trị</label
            >
            <b-form-tags
              :id="`option-values-${index + 1}`"
              v-model="option.optionValues"
              placeholder="Nhập giá trị và nhấn enter (V.d. Đen, Trắng, Xanh)"
              :tag-validator="validateTag"
              tag-variant="primary"
              class="mb-2"
              add-button-text="Thêm"
              tag-placeholder="Nhấn enter để thêm"
              duplicates-input-field="true"
              :state="option.touched ? validateOptionValues(index + 1) : null"
              @input="onTagsUpdate(index + 1, $event)"
              no-outer-focus
            ></b-form-tags>
            <b-form-invalid-feedback
              v-if="option.touched && !validateOptionValues(index + 1)"
            >
              Please add at least one value
            </b-form-invalid-feedback>

            <b-form-invalid-feedback
              v-if="option.touched && !validateOptionTitle(index + 1)"
            >
              Option title is required
            </b-form-invalid-feedback>

            <b-form-invalid-feedback
              v-if="option.touched && option.hasDuplicate"
            >
              This option title already exists. Please use a unique name.
            </b-form-invalid-feedback>
          </div>
          <div class="right ml-5 mr-5">
            <b-button
              variant="link"
              class="p-0 text-danger"
              @click="removeOption(index + 1)"
              aria-label="Remove option"
            >
              <b-icon icon="x-circle-fill" font-scale="1.2"></b-icon>
            </b-button>
          </div>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
export default {
  name: 'ProductOptions',
  props: {
    existingOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      productOptions: [
        {
          name: '',
          optionValues: [],
          hasDuplicate: false,
          touched: false,
        },
      ],
      isLoading: false,
    };
  },
  computed: {
    isValid() {
      return this.productOptions.every(
        (option, index) =>
          this.validateOptionTitle(index) &&
          this.validateOptionValues(index) &&
          !option.hasDuplicate,
      );
    },
    formattedOptions() {
      return this.productOptions.map((option) => ({
        name: option.name.trim(),
        optionValues: [...option.optionValues],
        ...(option.id ? { id: option.id } : {}),
      }));
    },
  },
  watch: {
    existingOptions: {
      handler(newOptions) {
        if (newOptions && newOptions.length > 0) {
          this.loadExistingOptions(newOptions);
        }
      },
      immediate: true,
    },
  },
  methods: {
    onTagsUpdate(index, values) {
      const updatedOptions = [...this.productOptions];

      updatedOptions[index] = {
        ...updatedOptions[index],
        optionValues: [...values],
        touched: true,
      };

      this.productOptions = updatedOptions;

      this.$nextTick(() => {
        this.$emit('update:options', this.formattedOptions);
      });
    },

    loadExistingOptions(options) {
      if (options && options.length > 0) {
        this.productOptions = options.map((option) => {
          let processedValues = [];

          if (Array.isArray(option.optionValues)) {
            if (
              option.optionValues.length > 0 &&
              typeof option.optionValues[0] === 'string'
            ) {
              processedValues = [...option.optionValues];
            } else if (
              option.optionValues.length > 0 &&
              typeof option.optionValues[0] === 'object'
            ) {
              processedValues = [
                ...option.optionValues.map((opt) =>
                  typeof opt.value !== 'undefined'
                    ? opt.value
                    : opt.toString
                    ? opt.toString()
                    : '',
                ),
              ];
            }
          }

          return {
            id: option.id,
            name: option.name,
            optionValues: processedValues,
            hasDuplicate: false,
            touched: false,
          };
        });
      }

      if (!this.productOptions || this.productOptions.length === 0) {
        this.productOptions = [
          {
            name: '',
            optionValues: [],
            hasDuplicate: false,
            touched: false,
          },
        ];
      }
    },
    addNewOption() {
      const newOptions = [...this.productOptions];

      newOptions.push({
        name: '',
        optionValues: [],
        hasDuplicate: false,
        touched: false,
      });

      this.productOptions = newOptions;

      this.$nextTick(() => {
        this.$emit('update:options', this.formattedOptions);
      });
    },
    removeOption(index) {
      if (index > 0) {
        const newOptions = [...this.productOptions];
        newOptions.splice(index, 1);

        this.productOptions = newOptions;

        this.$nextTick(() => {
          this.$emit('update:options', this.formattedOptions);
        });
      }
    },
    validateOptionTitle(index) {
      return this.productOptions[index].name.trim() !== '';
    },
    validateOptionValues(index) {
      return this.productOptions[index].optionValues.length > 0;
    },
    validateTag(tag) {
      return tag.trim() !== '' && tag.length <= 50;
    },
    checkDuplicateTitle(changedIndex) {
      const currentTitle = this.productOptions[changedIndex].name
        .trim()
        .toLowerCase();
      if (currentTitle === '') {
        const updatedOptions = [...this.productOptions];
        updatedOptions[changedIndex] = {
          ...updatedOptions[changedIndex],
          hasDuplicate: false,
        };
        this.productOptions = updatedOptions;
        return;
      }

      let hasDuplicate = false;
      for (let i = 0; i < this.productOptions.length; i++) {
        if (i === changedIndex) continue;

        const otherTitle = this.productOptions[i].name.trim().toLowerCase();
        if (currentTitle === otherTitle && otherTitle !== '') {
          hasDuplicate = true;
          break;
        }
      }

      const updatedOptions = [...this.productOptions];
      updatedOptions[changedIndex] = {
        ...updatedOptions[changedIndex],
        hasDuplicate,
      };
      this.productOptions = updatedOptions;
    },
    onOptionInput(index) {
      this.checkDuplicateTitle(index);
      this.$nextTick(() => {
        this.$emit('update:options', this.formattedOptions);
      });
    },
    onOptionBlur(index) {
      const updatedOptions = [...this.productOptions];
      updatedOptions[index] = {
        ...updatedOptions[index],
        touched: true,
      };
      this.productOptions = updatedOptions;

      this.checkDuplicateTitle(index);
      this.$nextTick(() => {
        this.$emit('update:options', this.formattedOptions);
      });
    },
  },
};
</script>

<style scoped>
/deep/ .card {
  background-color: #f3f6f9 !important;
}
</style>
